import { Injectable, Inject, PLATFORM_ID, Renderer2, RendererFactory2  } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class TagsInjectorService {
  private renderer: Renderer2;
  private isBrowser: boolean = false;
  private trackingContainer: HTMLElement | null = null;
  private addedTags: Set<string> = new Set();
  private injectedScripts: Set<string> = new Set();

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
    private sanitizer: DomSanitizer,
    rendererFactory: RendererFactory2

  ) {
    this.renderer = rendererFactory.createRenderer(null, null); 
    this.isBrowser = isPlatformBrowser(this.platformId);
  }


  injectScript(scriptConfig: { type: string; src?: string; content?: string }): void {
    if (!this.isBrowser) return;

    const sourceKey = scriptConfig.src || scriptConfig.content || '';
    if (!sourceKey) return;

    if (this.isJornayaScript(scriptConfig)) {
      this.logJornayaDebug('tags-injector:injectScript:called', {
        hasSrc: !!scriptConfig.src,
        hasContent: !!scriptConfig.content,
        src: scriptConfig.src || '',
      });
    }

    if (this.injectedScripts.has(sourceKey)) {
      if (this.isJornayaScript(scriptConfig)) {
        this.logJornayaDebug('tags-injector:injectScript:skipped-already-injected', {
          src: scriptConfig.src || '',
        });
      }
      return;
    }

    const isLeadIdScript =
      (scriptConfig.src && scriptConfig.src.includes('create.lidstatic.com/campaign')) ||
      (scriptConfig.content && scriptConfig.content.includes('create.lidstatic.com/campaign'));

    if (isLeadIdScript) {
      const hasLeadId =
        !!this.document.getElementById('LeadiDscript_campaign') ||
        !!this.document.querySelector("script[src*='create.lidstatic.com/campaign']");

      if (hasLeadId) {
        this.injectedScripts.add(sourceKey);
        this.logJornayaDebug('tags-injector:injectScript:skipped-existing-campaign', {
          src: scriptConfig.src || '',
        });
        return;
      }
    }

    const isTrustedFormScript =
      (scriptConfig.src && scriptConfig.src.includes('api.trustedform.com/trustedform.js')) ||
      (scriptConfig.content && scriptConfig.content.includes('api.trustedform.com/trustedform.js'));

    if (isTrustedFormScript) {
      const hasTrustedForm = !!this.document.querySelector("script[src*='api.trustedform.com/trustedform.js']");

      if (hasTrustedForm) {
        this.injectedScripts.add(sourceKey);
        return;
      }
    }

    const script = this.renderer.createElement('script');

    script.type = scriptConfig.type;
    script.setAttribute('data-dynamic', 'true');

    if (scriptConfig.src) {
      script.src = scriptConfig.src;
    } else if (scriptConfig.content) {
      script.text = scriptConfig.content;
    }

    this.renderer.appendChild(this.document.head, script);
    this.injectedScripts.add(sourceKey);

    if (isLeadIdScript) {
      this.logJornayaDebug('tags-injector:injectScript:appended-to-head', {
        src: scriptConfig.src || '',
      });
    }
  }

  clearScripts(): void {
    const existingScripts = this.document.head.querySelectorAll('script[data-dynamic]');
    existingScripts.forEach(script => script.remove());
  }

  private isJornayaScript(scriptConfig: { src?: string; content?: string }): boolean {
    const src = scriptConfig.src || '';
    const content = scriptConfig.content || '';
    return src.includes('lidstatic.com/campaign') ||
      src.includes('cloudfront.net/campaign') ||
      content.includes('LeadiDscript_campaign') ||
      content.includes('lidstatic.com/campaign') ||
      content.includes('cloudfront.net/campaign');
  }

  private isDebugJornayaEnabled(): boolean {
    const search = this.document?.location?.search || '';
    if (!search) return false;
    const value = new URLSearchParams(search).get('debugJornaya')?.trim().toLowerCase();
    return value === '1' || value === 'true' || value === 'yes';
  }

  private logJornayaDebug(stage: string, details: Record<string, unknown> = {}): void {
    if (!this.isDebugJornayaEnabled()) return;
    console.log(`[JORNAYA-DEBUG][tags-injector] stage=${stage}`, details);
  }

}
